// PageCarousel.jsx — autoplay enabled, keeps your arrows/divs
import React, { useEffect, useRef, useState } from "react";

export default function PageCarousel({
  slides = [],              // array of React nodes (each is a full "page")
  showDots = true,
  showArrows = true,
  className = "",
  ariaLabel = "Carousel",
  onIndexChange,            // optional: (idx) => void
  startIndex = 0,
  enableSwipe = true,

  // NEW:
  autoPlay = true,
  intervalMs = 3000,
  pauseOnHover = true,
}) {
  const safeSlides = Array.isArray(slides) ? slides.filter(Boolean) : [];
  const [idx, setIdx] = useState(
    Math.min(startIndex, Math.max(0, safeSlides.length - 1))
  );

  // NEW: timer + hover refs for autoplay
  const timerRef = useRef(null);
  const hoveringRef = useRef(false);

  // keep idx valid when slides length changes
  useEffect(() => {
    setIdx((i) => Math.min(i, Math.max(0, safeSlides.length - 1)));
  }, [safeSlides.length]);

  // notify parent on index change
  useEffect(() => {
    if (typeof onIndexChange === "function") onIndexChange(idx);
  }, [idx, onIndexChange]);

  // NEW: autoplay effect (guarded, no conditional hook calls)
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoPlay && safeSlides.length > 1) {
      timerRef.current = setInterval(() => {
        if (!hoveringRef.current) {
          setIdx((i) => (i + 1) % safeSlides.length);
        }
      }, intervalMs);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [autoPlay, intervalMs, safeSlides.length]);

  // swipe (manual)
  const startX = useRef(0);
  const onTouchStart = enableSwipe
    ? (e) => { startX.current = e.changedTouches[0].clientX; }
    : undefined;

  const onTouchEnd = enableSwipe
    ? (e) => {
        const dx = e.changedTouches[0].clientX - startX.current;
        if (Math.abs(dx) > 30) {
          setIdx((i) =>
            dx > 0
              ? (i - 1 + safeSlides.length) % safeSlides.length
              : (i + 1) % safeSlides.length
          );
        }
      }
    : undefined;

  if (!safeSlides.length) {
    return (
      <div
        className={`relative w-full select-none ${className}`}
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
      />
    );
  }

  // helpers
  const prev = () => setIdx((i) => (i - 1 + safeSlides.length) % safeSlides.length);
  const next = () => setIdx((i) => (i + 1) % safeSlides.length);

  return (
    <div
      className={` select-none ${className}`}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") prev();
        if (e.key === "ArrowRight") next();
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseEnter={pauseOnHover ? () => (hoveringRef.current = true) : undefined}
      onMouseLeave={pauseOnHover ? () => (hoveringRef.current = false) : undefined}
    >
      {/* side arrows */}
      {showArrows && safeSlides.length > 1 && (
        <>
          <div
            aria-label="Previous page"
            onClick={() =>
              setIdx((i) => (i - 1 + safeSlides.length) % safeSlides.length)
            }
            className="absolute cursor-pointer left-0 top-1/2 -translate-y-1/2 z-10 px-2 py-1 text-base rounded-md bg-transparent hover:bg-accent"
          >
            ‹
          </div>
          <div
            aria-label="Next page"
            onClick={() => setIdx((i) => (i + 1) % safeSlides.length)}
            className="absolute cursor-pointer right-0 top-1/2 -translate-y-1/2 z-10 px-2 py-1 text-base rounded-md bg-transparent hover:bg-accent"
          >
            ›
          </div>
        </>
      )}
      

      {/* slides viewport */}
      <div className="relative w-full overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {safeSlides.map((slide, i) => (
            <div key={i} className="w-full flex-shrink-0">
              {slide}
            </div>
          ))}
        </div>
      </div>

      {/* dots */}
      {showDots && safeSlides.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-1">
          {safeSlides.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to page ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-foreground/80" : "w-2 bg-foreground/20"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
